import {
    IExpressionResolver, IExpressionResolution, ResolutionErrorKind,
    successfulExpressionResolution, failedExpressionResolution, IResolutionError
} from 'app/model/expressions/expression-resolution';
import { Metamodel } from 'app/model/base/model';
import { ExpressionCategory, Expression, ScalarExpression } from './expression';
import { Owned } from 'app/model/base/named';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { Dataset } from 'app/model/dataset/dataset';
import { stringsAreEqualCaseInsensitive } from 'app/shared/utils/string.util';

/**
 * A resolver capable of parsing system variables such as: @pipeline().DataFactory, ...
 */
export class SystemVariableResolver implements IExpressionResolver {
    /**
     * Initializes a new instance of the SystemVariableResolver class.
     * @param _registeredExpressions The registered expressions to search for system variables within.
     */
    public constructor(private _registeredExpressions: Expression[]) {
    }

    public isOfType(expression: string, ignoreStart: boolean = false): boolean {
        expression = expression ? expression.trim() : '';
        return expression.startsWith(ignoreStart ? 'pipeline()' : '@pipeline()')
         && !expression.startsWith(ignoreStart ? 'pipeline().parameters' : '@pipeline().parameters')
         && !expression.startsWith(ignoreStart ? 'pipeline().TriggerEvent' : '@pipeline().TriggerEvent')
         && !expression.startsWith(ignoreStart ? 'pipeline()?.TriggerEvent' : '@pipeline()?.TriggerEvent')
         && !expression.startsWith(ignoreStart ? 'pipeline().libraryVariables' : '@pipeline().libraryVariables');
    }

    public resolve(expression: string, ignoreStart: boolean = false): IExpressionResolution {
        if (this.isOfType(expression, ignoreStart)) {
            const cleanedExpression = this.cleanExpression(expression, ignoreStart);
            const systemVariableMatch = this._registeredExpressions?.find(
                (exp) => exp.category === ExpressionCategory.SystemVariable && stringsAreEqualCaseInsensitive(exp.syntax, cleanedExpression)) as ScalarExpression;

            // These three are special unofficial variables. The official counterparts have the questionmark - e.g pipeline()?.TriggeredByPipelineName
            // We will show a warning to the user about their usage
            if (stringsAreEqualCaseInsensitive('pipeline().TriggeredByPipeline', cleanedExpression) ||
                stringsAreEqualCaseInsensitive('pipeline().TriggeredByPipelineName', cleanedExpression) ||
                stringsAreEqualCaseInsensitive('pipeline().TriggeredByPipelineRunId', cleanedExpression)) {
                return failedExpressionResolution(expression, {
                    kind: ResolutionErrorKind.SystemVariableNestedPipelineWarning,
                    details: { systemVariableName: cleanedExpression.replace('@', '') }
                }, undefined, undefined, true /*treatAsWarning*/);
            }

            return systemVariableMatch
                ? successfulExpressionResolution(expression, systemVariableMatch)
                : failedExpressionResolution(expression, { kind: ResolutionErrorKind.SystemVariableNotFound, details: { systemVariableName: cleanedExpression.replace('@', '') } });
        } else {
            return failedExpressionResolution(expression, { kind: ResolutionErrorKind.Unrecognized, details: expression });
        }
    }

    public checkApplicability(entity: Owned): { isApplicable: boolean, error?: IResolutionError } {
        return { isApplicable: Metamodel.modelOf(entity.concept) === Pipeline.MODEL || entity.concept instanceof Dataset };
    }

    private cleanExpression(expression: string, ignoreStart: boolean): string {
        expression = expression ? expression.trim() : '';
        expression = expression && !ignoreStart ? expression.slice(1) : expression; // take out the leading @ if this is a top level expression

        return expression;
    }
}
